import { el, clear } from '../core/dom.js';
import { currentRoute, navigate } from '../router.js';
import { getState } from '../core/state.js';

import { renderToday } from '../screens/today-screen.js';
import { renderRescue } from '../screens/rescue-screen.js';
import { renderProgress } from '../screens/progress-screen.js';
import { renderPlan } from '../screens/plan-screen.js';
import { renderSettings } from '../screens/settings-screen.js';
import { renderBackup } from '../screens/backup-screen.js';
import { renderOnboarding } from '../screens/onboarding-screen.js';
import { renderQuickStart } from '../onboarding/quick-start.js';
import { renderWelcome } from '../screens/welcome-screen.js';
import { renderReflection } from '../screens/reflection-screen.js';
import { renderPlans } from '../screens/plans-screen.js';
import { renderUrgeCommandCenter } from '../screens/urge-command-center.js';
import { renderResources } from '../screens/resources-screen.js';
import { icon, type IconName } from './icons.js';

interface NavItem { path: string; label: string; icon: IconName }
const NAV: NavItem[] = [
  { path: '/today', label: 'Now', icon: 'today' },
  { path: '/rescue', label: 'Rescue', icon: 'rescue' },
  { path: '/progress', label: 'Progress', icon: 'progress' },
  { path: '/plan', label: 'Plan', icon: 'plans' },
  { path: '/settings', label: 'Settings', icon: 'settings' },
];

/** Targets that should preserve the user's active habit across nav clicks. */
const HABIT_AWARE_PATHS = new Set(['/plan', '/progress', '/reflect']);

export function renderShell(root: HTMLElement) {
  clear(root);
  const st = getState();
  const onboarded = !!st.meta.onboarding_complete && !!st.activeHabit && !!st.activePlan;
  const route = currentRoute().path;

  if (!onboarded) {
    const main = el('main', { class: 'app-main' });
    const shell = el('div', { class: 'app-shell' }, main);
    root.appendChild(shell);
    if (route === '/onboard') {
      // Advanced wizard available via ?advanced=1 for users who want the full
      // 15-step setup. Default is the new quick-start.
      const params = currentRoute().params;
      if (params.get('advanced') === '1') renderOnboarding(main);
      else renderQuickStart(main);
    } else if (route === '/rescue') {
      // Emergency rescue path: users can run rescue before completing onboarding.
      renderRescue(main);
    } else if (route === '/resources') {
      renderResources(main);
    } else if (route === '/settings') {
      renderSettings(main);
    } else if (route === '/backup') {
      renderBackup(main);
    } else renderWelcome(main);
    return;
  }

  const currentParams = currentRoute().params;
  const currentHabit = currentParams.get('habit') ?? undefined;
  function navAnchor(n: NavItem, opts: { size?: number } = {}): HTMLAnchorElement {
    const habit = HABIT_AWARE_PATHS.has(n.path) ? currentHabit : undefined;
    const href = habit ? `#${n.path}?habit=${encodeURIComponent(habit)}` : `#${n.path}`;
    const a = el('a', {
      href,
      onclick: (e: Event) => {
        if (!HABIT_AWARE_PATHS.has(n.path)) return;
        if (!habit) return;
        e.preventDefault();
        navigate(n.path, { habit });
      },
    });
    a.appendChild(icon(n.icon, opts.size ? { class: 'nav-icon', size: opts.size } : { class: 'nav-icon' }));
    a.appendChild(el('span', { class: 'nav-label' }, n.label));
    return a;
  }
  const side = el('nav', { class: 'nav-side', attrs: { 'aria-label': 'Primary' } },
    el('div', { class: 'brand' }, el('span', { class: 'brand-dot' }), 'Wean'),
    ...NAV.map((n) => navAnchor(n)),
    el('a', { href: '#/backup', class: 'tag', attrs: { style: 'margin-top:auto;' } }, 'Backup'),
    el('a', { href: '#/plans', class: 'tag' }, 'Manage plans'),
    el('a', { href: '#/resources', class: 'tag' }, 'Support'),
  );
  const bottom = el('nav', { class: 'nav-bottom', attrs: { 'aria-label': 'Sections' } },
    ...NAV.map((n) => navAnchor(n, { size: 20 })),
  );
  const main = el('main', { class: 'app-main', id: 'main', attrs: { tabindex: '-1' } });
  const shell = el('div', { class: 'app-shell' }, side, main, bottom);
  const skip = el('a', {
    class: 'skip-link', attrs: { href: '#main' },
    onclick: (e: Event) => {
      e.preventDefault();
      main.focus();
    },
  }, 'Skip to main content');
  root.appendChild(skip);
  root.appendChild(shell);

  setActiveNav(side, bottom, route);
  if (route.startsWith('/urge/')) {
    renderUrgeCommandCenter(main);
    return;
  }
  switch (route) {
    case '/today': renderToday(main); break;
    case '/rescue': renderRescue(main); break;
    case '/progress': renderProgress(main); break;
    case '/plans': renderPlans(main); break;
    case '/plan': renderPlan(main); break;
    case '/settings': renderSettings(main); break;
    case '/backup': renderBackup(main); break;
    case '/reflect': renderReflection(main); break;
    case '/resources': renderResources(main); break;
    case '/onboard':
      if (currentParams.get('advanced') === '1') renderOnboarding(main);
      else renderQuickStart(main);
      break;
    default: navigate('/today');
  }
}

function setActiveNav(side: HTMLElement, bottom: HTMLElement, path: string) {
  for (const a of [...side.querySelectorAll('a'), ...bottom.querySelectorAll('a')]) {
    // Habit-aware anchors carry a `?habit=...` suffix on their href, so the
    // raw strict-equality check would miss them. Compare just the path part.
    const href = a.getAttribute('href') ?? '';
    const p = (href.split('?')[0] ?? '').replace(/^#/, '');
    a.classList.toggle('active', p === path);
  }
}
